import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { Result } from "senf-atomic-design-system/src/components/special/survey/legacy/survey.types";
import type {
  InputColumn,
  InputRow,
  Page,
  PageType,
} from "senf-atomic-design-system/src/components/special/survey/survey.config";
import { capitalizeFirstLetter } from "./helpers";
import i18n from "./i18n";

export const initGenerateSliderLabels = (t: TFunction) => {
  type BaseProps = {
    left?: string;
    right?: string;
    numeric?: boolean;
    min?: number;
  };
  type CountProps = BaseProps & { count: number };
  type ColumnProps = BaseProps & { columns: InputColumn[] };

  function generateLabels(props: CountProps): string[];
  function generateLabels(props: ColumnProps): string[];
  function generateLabels({
    columns,
    numeric,
    left,
    right,
    count,
    min = 1,
  }: Partial<ColumnProps & CountProps>): string[] {
    const otherOption = columns?.find((f) => f.uid === "other");
    const colCount =
      columns && otherOption ? columns.length - 1 : columns?.length;
    const n = count || colCount || 3;
    if (n > 10 || numeric) return [...Array(n).keys()].map((e) => `${e + min}`);
    let scale: string[] = [];
    const map = [0, 4, 2, 1, 5, 3, 6]; // this is the order in which the intensities are displayed
    const intensities = (string: string) => {
      const val = string.replace(/ /g, "**");
      return [
        // this is the order in which the intensities are inserted
        t("survey:sliderSteps.neutral"), // 0
        val ? capitalizeFirstLetter(val) : t("survey:sliderSteps.completely"),
        t("survey:sliderSteps.rather", { val }),
        t("survey:sliderSteps.very", { val }),
        t("survey:sliderSteps.somewhat", { val }),
        t("survey:sliderSteps.quite", { val }),
        t("survey:sliderSteps.extremely", { val }), // 6
      ].map((_, i, arr) => {
        const l = Math.floor(n / 2) + 1;
        const val = arr.slice(0, l)[map[i]] || "";
        const short = string.split(" ").length === 1;
        return (short ? val : val.replace(/^([^ ]*) /, "$1: ")).replace(
          /\*\*/g,
          " ",
        );
      });
    };

    for (let i = -6; i <= 6; i++) {
      scale.push(intensities(i < 0 ? left || "" : right || "")[Math.abs(i)]);
    }
    scale = scale.filter(Boolean);
    if (!(n % 2)) scale.splice(Math.floor(scale.length / 2), 1);

    if (otherOption) scale.push(otherOption.label || t("global:other"));

    return scale;
  }
  return generateLabels;
};
export function useGenerateSliderLabels() {
  const { t } = useTranslation();
  return initGenerateSliderLabels(t);
}

export const getOptionsAndColumns = (
  question: Page,
  tagCol?: any,
): {
  options: InputRow<PageType>[];
  columns: InputColumn<PageType>[];
} => {
  let { rows, columns } = question.options || {
    rows: [],
    columns: [],
  };
  if (
    question.type === "choice" ||
    question.type === "choiceImage" ||
    question.type === "budgetAllocation" ||
    ((question.type === "mapPoint" ||
      question.type === "mapPath" ||
      question.type === "mapArea") &&
      question.settings?.valueType === "boolean")
  ) {
    columns = question.options?.rows || [];
    rows = question.options?.columns || [];
  }
  if (question.settings?.otherOption?.enabled) {
    rows = [
      ...rows,
      {
        uid: "other",
        label: question.settings.otherOption?.label || i18n.t("global:other"),
      },
    ];
  }
  console.log("rows", rows, columns);
  const c: InputColumn<PageType>[] = tagCol
    ? (tagCol?.values || []).map((e: any) => ({
        ...e,
        uid: e.value,
        // keep label for display; consumers expecting InputColumn only read uid/label
        label: e.label,
      }))
    : columns;
  const o: InputRow<PageType>[] = tagCol
    ? [
        {
          uid: tagCol.value,
          label: tagCol.label,
        },
      ]
    : [...((rows as InputRow<PageType>[]) || [])].filter(Boolean);

  return {
    options: (c?.length ? o : [{ uid: "", label: "" }]) as InputRow<PageType>[],
    columns: (c?.length ? c : o) as InputColumn<PageType>[],
  };
};

type AverageProps = {
  results: Result[];
  question: Page;
  row: InputRow<PageType>;
};

export const getAverageRating = ({ results, question, row }: AverageProps) => {
  if (!row) return 0;
  const { columns } = getOptionsAndColumns(question);
  const res = results.map((e) => {
    const value = e.values[question.uid]?.value;
    const i = columns?.findIndex(
      (f) =>
        f?.uid ===
        (value?.rating ||
          (typeof value === "string" ? value : value?.[row.uid])),
    );
    if ((!i && i !== 0) || i === -1) return 0;
    return i + 1;
  });
  const sum = res.reduce((a, b) => a + b, 0);
  return sum / (res.filter(Boolean)?.length || 1);
};
